import { useState, useEffect, useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { ArrowUpRight, Clock, Award } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { apiGet } from '../../../lib/apiClient'

interface Course {
  id: string
  title: string
  description: string
  image?: string
  duration: string
  price: number
  level: string
}

export default function Courses() {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<Course[]>([])
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await apiGet('/courses?published=true', { skipAuth: true })
        if (response.ok) {
          const data = await response.json()
          setCourses((data.courses || []).map((c: any) => ({
            id: c._id,
            title: c.title,
            description: c.description,
            image: c.thumbnail || c.image,
            duration: c.duration,
            price: c.price,
            level: c.level,
          })))
        }
      } catch (error) {
        console.error('Error:', error)
      }
    }
    fetchCourses()
  }, [])

  return (
    <section 
      id="courses" 
      ref={ref} 
      className="relative bg-[#FDFBF7] dark:bg-neutral-950 py-32 overflow-hidden transition-colors duration-500"
      aria-labelledby="courses-heading"
      itemScope
      itemType="https://schema.org/ItemList"
    >
      <meta itemProp="name" content="Beauty & Makeup Courses at Sudha's Academy" />
      <meta itemProp="description" content="Professional beauty training courses including bridal makeup, skincare, hair styling, and cosmetology" />
      <meta itemProp="numberOfItems" content={courses.length.toString()} />
      
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <header className="flex justify-between items-end mb-16">
          <div>
            <span className="text-rose-500 font-mono text-xs uppercase tracking-[0.3em] mb-4 block">
              Curriculum
            </span>
            <h2 
              id="courses-heading"
              className="font-serif text-4xl md:text-5xl text-charcoal-900 dark:text-white leading-tight"
            >
              Signature Courses
            </h2>
            <p className="text-charcoal-600 dark:text-neutral-400 mt-4 max-w-xl">
              Industry-recognized beauty and makeup training programs designed by experts with hands-on practical experience.
            </p>
          </div>
          <button
            onClick={() => navigate('/user/courses')}
            className="hidden md:flex items-center gap-2 text-charcoal-600 dark:text-neutral-400 hover:text-rose-500 dark:hover:text-rose-400 transition-colors uppercase text-xs tracking-widest font-medium"
            aria-label="View all beauty and makeup courses"
          >
            View All Courses <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </header>

        {/* Horizontal Scroll Container */}
        <div 
          className="flex gap-8 overflow-x-auto pb-12 scrollbar-hide snap-x"
          role="list"
          aria-label="Featured courses"
        >
          {courses.map((course, index) => (
            <motion.article
              key={course.id}
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              onClick={() => navigate('/user/courses')}
              className="min-w-[300px] md:min-w-[400px] snap-center group cursor-pointer"
              role="listitem"
              itemScope
              itemType="https://schema.org/Course"
              itemProp="itemListElement"
            >
              <meta itemProp="position" content={(index + 1).toString()} />
              
              <figure className="relative aspect-[4/3] overflow-hidden rounded-2xl mb-6">
                <img
                  src={course.image || '/placeholder.jpg'}
                  alt={`${course.title} - ${course.level} level beauty course at Sudha's Academy`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  itemProp="image"
                  loading="lazy"
                  width="400"
                  height="300"
                />
                <div 
                  className="absolute top-4 right-4 bg-white/90 dark:bg-black/80 backdrop-blur px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider text-charcoal-900 dark:text-white"
                  itemProp="educationalLevel"
                >
                  {course.level}
                </div>
              </figure>

              <h3 
                className="text-2xl font-serif font-bold text-charcoal-900 dark:text-white mb-2 group-hover:text-rose-500 dark:group-hover:text-rose-400 transition-colors"
                itemProp="name"
              >
                {course.title}
              </h3>
              
              <p className="sr-only" itemProp="description">
                {course.description || `Professional ${course.title} training at Sudha's Academy. Learn from expert instructors with hands-on practical experience.`}
              </p>

              <div className="flex items-center gap-6 text-charcoal-500 dark:text-neutral-500 text-sm mb-4">
                <div className="flex items-center gap-2" itemProp="timeRequired">
                  <Clock className="w-4 h-4" aria-hidden="true" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4" aria-hidden="true" />
                  <span itemProp="educationalCredentialAwarded">Certificate</span>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-charcoal-100 dark:border-white/10 pt-4">
                <span 
                  className="font-bold text-lg text-charcoal-900 dark:text-white"
                  itemProp="offers"
                  itemScope
                  itemType="https://schema.org/Offer"
                >
                  <meta itemProp="priceCurrency" content="INR" />
                  <span itemProp="price">₹{course.price?.toLocaleString()}</span>
                  <meta itemProp="availability" content="https://schema.org/InStock" />
                </span>
                <span 
                  className="w-8 h-8 rounded-full bg-charcoal-100 dark:bg-white/10 flex items-center justify-center group-hover:bg-rose-500 dark:group-hover:bg-rose-500 group-hover:text-white transition-colors"
                  aria-hidden="true"
                >
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
              
              {/* Provider info for schema */}
              <span itemProp="provider" itemScope itemType="https://schema.org/EducationalOrganization" className="hidden">
                <meta itemProp="name" content="Sudha's Academy" />
                <meta itemProp="url" content="https://sudhasacademy.com" />
              </span>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  )
}
